import { useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import {
  AlertTriangle,
  CheckCircle2,
  Lightbulb,
  Loader2,
  RefreshCw,
  TrendingUp,
} from 'lucide-react';
import {
  useCsAiAnalysisLatest,
  useCsAiAnalysisHistory,
  useRunCsAiAnalysis,
} from '../hooks/useCsInquiry.js';
import { csInquiryApi } from '../lib/api.js';
import type { CsAiAnalysisHistoryItem, SavedCsAiAnalysisResponse } from '../types/cs-inquiry.js';
import { ChatAnalysisRow, InsightCard } from '../components/ai-analysis/CsAiAnalysisWidgets.js';
import { UnfetchedChatsBanner } from '../components/cs-inquiry/UnfetchedChatsBanner.js';
import {
  AiAnalysisHistoryTable,
  formatAnalyzedAt,
} from '../components/ai-analysis/AiAnalysisHistoryTable.js';
/** @graph-connects none */
const CATEGORY_COLORS = [
  '#0017C1',
  '#3460FB',
  '#7096F8',
  '#FE3939',
  '#FF8C42',
  '#22C55E',
  '#A855F7',
  '#C5D7FB',
] as const;

/** @graph-connects none */
const SENTIMENT_COLORS = { positive: '#22C55E', neutral: '#94A3B8', negative: '#EF4444' };

/** @graph-connects none */
const QUALITY_SCORES = [1, 2, 3, 4, 5] as const;

/** @graph-connects none */
const QUALITY_LABEL_KEYS: Record<(typeof QUALITY_SCORES)[number], string> = {
  1: 'csAiAnalysis.quality1',
  2: 'csAiAnalysis.quality2',
  3: 'csAiAnalysis.quality3',
  4: 'csAiAnalysis.quality4',
  5: 'csAiAnalysis.quality5',
};

/** カテゴリ分布ツールチップの表示フォーマッタを生成する。
 * @graph-connects none
 */
export const createCategoryTooltipFormatter =
  (t: TFunction) =>
  (v: unknown): [string, string] => [
    t('csAiAnalysis.countUnit', { num: String(v) }),
    t('csAiAnalysis.countLabel'),
  ];

/** 応対品質スコア分布ツールチップの表示フォーマッタを生成する。
 * @graph-connects none
 */
export const createQualityTooltipFormatter =
  (t: TFunction) =>
  (v: unknown, _n: unknown, item: { payload?: { label?: string } }): [string, string] => [
    t('csAiAnalysis.countUnit', { num: String(v) }),
    item.payload?.label ?? '',
  ];

/** @graph-connects none */
function defaultFrom(): string {
  const d = new Date(Date.now() + 9 * 60 * 60 * 1000);
  d.setUTCDate(d.getUTCDate() - 30);
  return d.toISOString().slice(0, 10);
}

/** @graph-connects none */
function defaultTo(): string {
  const d = new Date(Date.now() + 9 * 60 * 60 * 1000);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/** @graph-connects none */
type ViewMode = 'latest' | 'history';

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 問い合わせ AI 分析ページ。保存済み分析を即時表示し、手動実行ボタンで Gemini 分析を実行・保存。過去の分析履歴を閲覧可能。状態を取り込めていないチャットがあれば見出しと操作の下に帯を出す。
 * @graph-connects useCsAiAnalysisLatest [calls] 最新分析結果取得フック
 * @graph-connects useCsAiAnalysisHistory [calls] 分析履歴取得フック
 * @graph-connects useRunCsAiAnalysis [calls] 分析実行ミューテーション
 * @graph-connects UnfetchedChatsBanner [calls] 未取得の帯
 */
export function CsInquiryAiAnalysisPage() {
  const { t } = useTranslation();
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [viewMode, setViewMode] = useState<ViewMode>('latest');
  const [selectedData, setSelectedData] = useState<SavedCsAiAnalysisResponse | null>(null);

  const latestQuery = useCsAiAnalysisLatest();
  const historyQuery = useCsAiAnalysisHistory();
  const runMutation = useRunCsAiAnalysis();

  const data = selectedData ?? latestQuery.data ?? null;
  const isRunning = runMutation.isPending;

  const handleRun = () => {
    setSelectedData(null);
    setViewMode('latest');
    runMutation.mutate(
      { from, to },
      {
        onSuccess: (result) => {
          setSelectedData(result);
        },
      },
    );
  };

  const handleSelectHistory = (item: CsAiAnalysisHistoryItem) => {
    void csInquiryApi.getAiAnalysisDetail(item.id).then((detail) => {
      if (detail) {
        setSelectedData(detail);
        setViewMode('latest');
      }
    });
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-bold text-gray-900">{t('csAiAnalysis.title')}</h1>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm">
            <input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
              }}
              className="rounded border px-2 py-1"
            />
            <span className="text-gray-400">~</span>
            <input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
              }}
              className="rounded border px-2 py-1"
            />
          </div>
          <button
            type="button"
            onClick={handleRun}
            disabled={isRunning}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isRunning ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {t('csAiAnalysis.runAnalysis')}
          </button>
          <div className="flex rounded-lg border">
            <button
              type="button"
              onClick={() => {
                setViewMode('latest');
              }}
              className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'latest' ? 'bg-gray-100 text-gray-900' : 'text-gray-500'}`}
            >
              {t('csAiAnalysis.latestResult')}
            </button>
            <button
              type="button"
              onClick={() => {
                setViewMode('history');
              }}
              className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'history' ? 'bg-gray-100 text-gray-900' : 'text-gray-500'}`}
            >
              {t('csAiAnalysis.historyTab')}
            </button>
          </div>
        </div>
      </div>

      <UnfetchedChatsBanner page="aiAnalysis" />

      {runMutation.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          <AlertTriangle className="mb-2 h-5 w-5" />
          <p>{t('csAiAnalysis.runError')}</p>
        </div>
      )}

      {viewMode === 'history' ? (
        <Card className="p-4">
          <h2 className="mb-4 font-semibold text-gray-900">{t('csAiAnalysis.history')}</h2>
          {historyQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <AiAnalysisHistoryTable
              items={historyQuery.data?.items ?? []}
              onSelect={handleSelectHistory}
              getSummary={(item) => item.executiveSummary}
            />
          )}
        </Card>
      ) : isRunning ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <Loader2 className="mb-4 h-8 w-8 animate-spin" />
          <p className="text-sm">{t('csAiAnalysis.analyzing')}</p>
          <p className="text-xs text-gray-300">{t('csAiAnalysis.analyzeHint')}</p>
        </div>
      ) : latestQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : data ? (
        <>
          {/* Executive Summary */}
          <Card className="border-l-4 border-l-blue-500 p-5">
            <div className="mb-2 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-500" />
              <h2 className="font-semibold text-gray-900">{t('csAiAnalysis.summaryTitle')}</h2>
              <span className="text-xs text-gray-400">
                {t('csAiAnalysis.analyzedRatio', {
                  analyzed: String(data.analyzedChats),
                  total: String(data.totalChats),
                })}
              </span>
              {data.analyzedAt && (
                <span className="ml-auto text-xs text-gray-400">
                  {t('csAiAnalysis.executedAt', { time: formatAnalyzedAt(data.analyzedAt) })}
                </span>
              )}
            </div>
            <p className="text-sm leading-relaxed text-gray-700">{data.executiveSummary}</p>
          </Card>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Card className="p-4">
              <p className="text-xs text-gray-500">{t('csAiAnalysis.analyzedTarget')}</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">
                {t('csAiAnalysis.countUnit', { num: String(data.analyzedChats) })}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-gray-500">{t('csAiAnalysis.qualityScore')}</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">
                {data.responseQuality.averageScore.toFixed(1)}
                <span className="text-sm text-gray-400"> / 5.0</span>
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-gray-500">{t('csAiAnalysis.categoryCount')}</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{data.categories.length}</p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-gray-500">{t('csAiAnalysis.insightsLabel')}</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">
                {t('csAiAnalysis.countUnit', { num: String(data.insights.length) })}
              </p>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Category Distribution */}
            <Card className="p-5">
              <h3 className="mb-4 font-semibold text-gray-900">{t('csAiAnalysis.categoryDist')}</h3>
              {data.categories.length > 0 ? (
                <div className="flex items-start gap-4">
                  <div className="w-[200px] flex-shrink-0">
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={data.categories.map((cat, i) => ({
                            ...cat,
                            fill: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
                          }))}
                          dataKey="count"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          innerRadius={40}
                        />
                        <Tooltip formatter={createCategoryTooltipFormatter(t)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <ul className="flex-1 space-y-1.5 text-sm">
                    {data.categories.map((cat, i) => (
                      <li key={cat.name} className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <span
                            className="inline-block h-3 w-3 rounded-full"
                            style={{
                              backgroundColor: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
                            }}
                          />
                          {cat.name}
                        </span>
                        <span className="text-gray-500">
                          {t('csAiAnalysis.countWithPercent', {
                            num: String(cat.count),
                            pct: String(cat.percentage),
                          })}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="py-8 text-center text-sm text-gray-400">{t('common.noData')}</p>
              )}
            </Card>

            {/* Sentiment + Quality */}
            <Card className="p-5">
              <h3 className="mb-4 font-semibold text-gray-900">
                {t('csAiAnalysis.sentimentQuality')}
              </h3>
              <div className="space-y-4">
                {/* Sentiment Bar */}
                <div>
                  <p className="mb-1.5 text-xs text-gray-500">{t('csAiAnalysis.sentimentDist')}</p>
                  <div className="flex h-6 overflow-hidden rounded-full">
                    {(['positive', 'neutral', 'negative'] as const).map((key) => {
                      const total =
                        data.sentimentDistribution.positive +
                        data.sentimentDistribution.neutral +
                        data.sentimentDistribution.negative;
                      const pct = total > 0 ? (data.sentimentDistribution[key] / total) * 100 : 0;
                      return pct > 0 ? (
                        <div
                          key={key}
                          className="flex items-center justify-center text-xs font-medium text-white"
                          style={{
                            width: `${String(pct)}%`,
                            backgroundColor: SENTIMENT_COLORS[key],
                          }}
                        >
                          {pct >= 15 ? `${String(Math.round(pct))}%` : ''}
                        </div>
                      ) : null;
                    })}
                  </div>
                  <div className="mt-1 flex gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
                      {t('csAiAnalysis.sentimentPositive')} {data.sentimentDistribution.positive}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block h-2 w-2 rounded-full bg-gray-400" />
                      {t('csAiAnalysis.sentimentNeutral')} {data.sentimentDistribution.neutral}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block h-2 w-2 rounded-full bg-red-500" />
                      {t('csAiAnalysis.sentimentNegative')} {data.sentimentDistribution.negative}
                    </span>
                  </div>
                </div>

                {/* Quality Distribution Bar Chart */}
                <div>
                  <p className="mb-1.5 text-xs text-gray-500">
                    {t('csAiAnalysis.qualityScoreDist')}
                  </p>
                  <ResponsiveContainer width="100%" height={120}>
                    <BarChart
                      data={QUALITY_SCORES.map((score) => ({
                        score: t('csAiAnalysis.scorePoint', { num: String(score) }),
                        count: data.responseQuality.distribution[String(score)] ?? 0,
                        label: t(QUALITY_LABEL_KEYS[score]),
                      }))}
                    >
                      <XAxis dataKey="score" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip formatter={createQualityTooltipFormatter(t)} />
                      <Bar dataKey="count" fill="#3460FB" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Card>
          </div>

          {/* Quality Strengths & Improvements */}
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="p-5">
              <h3 className="mb-3 flex items-center gap-2 font-semibold text-green-700">
                <CheckCircle2 className="h-4 w-4" />
                {t('csAiAnalysis.strengths')}
              </h3>
              <ul className="space-y-2">
                {data.responseQuality.strengths.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-0.5 text-green-500">+</span>
                    {s}
                  </li>
                ))}
              </ul>
            </Card>
            <Card className="p-5">
              <h3 className="mb-3 flex items-center gap-2 font-semibold text-orange-700">
                <AlertTriangle className="h-4 w-4" />
                {t('csAiAnalysis.improvements')}
              </h3>
              <ul className="space-y-2">
                {data.responseQuality.improvements.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-0.5 text-orange-500">!</span>
                    {s}
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {/* Insights */}
          {data.insights.length > 0 && (
            <Card className="p-5">
              <h3 className="mb-4 flex items-center gap-2 font-semibold text-gray-900">
                <Lightbulb className="h-5 w-5 text-yellow-500" />
                {t('csAiAnalysis.insightsLabel')}
              </h3>
              <div className="grid gap-3 lg:grid-cols-2">
                {[...data.insights]
                  .sort((a, b) => {
                    const order = { high: 0, medium: 1, low: 2 };
                    return (order[a.priority] ?? 2) - (order[b.priority] ?? 2);
                  })
                  .map((insight, i) => (
                    <InsightCard key={i} insight={insight} />
                  ))}
              </div>
            </Card>
          )}

          {/* Per-Chat Analysis Table */}
          <Card className="p-5">
            <h3 className="mb-4 font-semibold text-gray-900">
              {t('csAiAnalysis.perChatTitle')}
              <span className="ml-2 text-sm font-normal text-gray-400">
                {t('csAiAnalysis.countUnit', { num: String(data.chatAnalyses.length) })}
              </span>
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b text-xs text-gray-500">
                    <th className="px-3 py-2 font-medium">{t('csAiAnalysis.colSummary')}</th>
                    <th className="px-3 py-2 font-medium">{t('csAiAnalysis.colCategory')}</th>
                    <th className="px-3 py-2 font-medium">{t('csAiAnalysis.colSubcategory')}</th>
                    <th className="px-3 py-2 text-center font-medium">
                      {t('csAiAnalysis.colQuality')}
                    </th>
                    <th className="px-3 py-2 text-center font-medium">
                      {t('csAiAnalysis.colUrgency')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.chatAnalyses.map((chat) => (
                    <ChatAnalysisRow key={chat.userChatId} chat={chat} />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ) : (
        <Card className="p-12 text-center">
          <p className="text-sm text-gray-500">{t('csAiAnalysis.noAnalysis')}</p>
          <p className="mt-1 text-xs text-gray-400">{t('csAiAnalysis.promptAnalyze')}</p>
        </Card>
      )}
    </div>
  );
}
